import { useState } from 'react'

import { clsx } from 'clsx'

import {
  ModerationItem,
  Post,
  Image,
  ModerationStatus,
  MassActionRequestContext,
  MemberListFilterByOperator,
  ModerationEntityType,
  ReportCategory,
  MassActionRequest,
  PostStatus,
  ModerationOpMode,
} from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useCreateMassActionRequest,
  useCreateModerationReport,
  useDeleteMember,
  useRemoveSpaceMember,
  useSuspendMember,
  useUpdateMember,
  useUpdateModerationItem,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Checkbox } from '@tribeplatform/react-ui-kit/Checkbox'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { Select } from '@tribeplatform/react-ui-kit/Select'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MassActionRequestAction } from '../../Admin/MassActions/types.js'

export interface PostRejectedModalProps {
  open: boolean
  onClose: () => void
  moderationItem: ModerationItem
  setMassActionRequest: (value: MassActionRequest) => void
}

type DeleteAuthorState =
  | 'delete'
  | 'suspend'
  | 'flag'
  | 'remove'
  | 'report'
  | 'noop'

export const PostRejectedModal = ({
  open,
  onClose,
  moderationItem,
  setMassActionRequest,
}: PostRejectedModalProps) => {
  const { $t } = useI18n()
  const { mutateAsync: updateModerationItem } = useUpdateModerationItem()
  const { mutateAsync: suspendMember } = useSuspendMember()
  const { mutateAsync: deleteMember } = useDeleteMember()
  const { mutateAsync: updateMember } = useUpdateMember()
  const { mutateAsync: createMassActionRequest } = useCreateMassActionRequest()
  const { mutateAsync: removeSpaceMember } = useRemoveSpaceMember()
  const { mutateAsync: createModerationReport } = useCreateModerationReport()
  const { isModerator } = useAuthMember()
  const [deleteOtherPosts, setDeleteOtherPosts] = useState<boolean>(false)
  const [deleteAuthor, setDeleteAuthor] = useState<DeleteAuthorState>('noop')
  const [loading, setLoading] = useState<boolean>(false)

  const post = moderationItem?.entity as Post
  const profilePicture = (post?.owner?.member?.profilePicture as Image)?.url
  const spaceId = post?.spaceId
  const name = post?.owner?.member?.name
  const memberId = post?.owner?.member?.id

  const moderatorActions = [
    {
      value: 'noop',
      text: $t({
        defaultMessage: 'No Action',
        id: 'Moderation.Action.Noop',
      }),
    },
    {
      value: 'delete',
      text: $t({
        defaultMessage: 'Delete author',
        id: 'Moderation.Action.Delete',
      }),
    },
    {
      value: 'suspend',
      text: $t({
        defaultMessage: 'Suspend author',
        id: 'Moderation.Action.Suspend',
      }),
    },
    {
      value: 'flag',
      text: $t({
        defaultMessage: 'Flag author',
        id: 'Moderation.Action.Flag',
      }),
    },
  ]

  const memberActions = [
    {
      value: 'noop',
      text: $t({
        defaultMessage: 'No Action',
        id: 'Moderation.Action.Noop',
      }),
    },
    {
      value: 'remove',
      text: $t({
        defaultMessage: 'Remove author from this space',
        id: 'Moderation.Action.Remove',
      }),
    },
    {
      value: 'report',
      text: $t({
        defaultMessage: 'Report author to community admins',
        id: 'Moderation.Action.Report',
      }),
    },
  ]

  const rejectContent = async () => {
    setLoading(true)
    try {
      await updateModerationItem({
        id: moderationItem.id,
        input: {
          status: ModerationStatus.ACCEPTED,
          opMode: ModerationOpMode.Purge,
        },
      })

      if (deleteAuthor === 'suspend') {
        await suspendMember({
          memberId,
        })
      }
      if (deleteAuthor === 'delete') {
        await deleteMember({
          id: memberId,
        })
      }
      if (deleteAuthor === 'flag') {
        await updateMember({
          id: memberId,
          input: {
            flagged: true,
          },
        })
      }
      if (deleteAuthor === 'remove') {
        await removeSpaceMember({
          memberIds: [memberId],
          spaceId,
        })
      }
      if (deleteAuthor === 'report') {
        await createModerationReport({
          input: {
            entityId: memberId,
            entityType: ModerationEntityType.MEMBER,
            reportCategory: ReportCategory.SPAM,
          },
        })
      }
      if (deleteOtherPosts) {
        const massActionRequest = await createMassActionRequest({
          input: {
            action: MassActionRequestAction.Delete,
            context: MassActionRequestContext.Post,
            extraProperties: {
              includeReplies: true,
            },
            filters: {
              filterBy: [
                {
                  key: 'ownerId',
                  operator: MemberListFilterByOperator.equals,
                  value: JSON.stringify(memberId),
                },
                {
                  key: 'status',
                  operator: MemberListFilterByOperator.in,
                  value: JSON.stringify([
                    PostStatus.PUBLISHED,
                    PostStatus.BLOCKED,
                  ]),
                },
              ],
            },
          },
        })
        setMassActionRequest(massActionRequest)
      }

      toast({
        title: $t({
          defaultMessage: 'Post has been deleted',
          id: 'Moderation.Action.Reject.Success',
        }),
        status: 'success',
      })
      onClose()
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Moderation.Action.Reject.Fail',
        }),
        status: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Content>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center">
            <Avatar size="3x" src={profilePicture} />
            <p className="text-content text-xl font-medium text-center">
              {name}
            </p>
            <p className="text-content-subdued text-center">
              <T
                defaultMessage="You are deleting {owner}'s content."
                id="Moderation.PostRejected.Description"
                values={{ owner: name }}
              />
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <FormControl.Label>
              {isModerator && (
                <T
                  defaultMessage="Do you want to delete, suspend, or flag the author?"
                  id="Moderation.AdditionalActions"
                />
              )}
              {!isModerator && (
                <T
                  defaultMessage="Do you want to report or remove the author from this space?"
                  id="Moderation.SpaceAdmin.AdditionalActions"
                />
              )}
            </FormControl.Label>

            <Select
              value={deleteAuthor}
              onChange={v => setDeleteAuthor(v as DeleteAuthorState)}
              items={isModerator ? moderatorActions : memberActions}
            />
          </div>
          {isModerator && (
            <div className="flex flex-col gap-3">
              <FormControl.Label>
                <T
                  defaultMessage="Would you like to delete all other posts by this {MEMBER}?"
                  id="Moderation.Post.DeleteAllPosts.Description"
                />
              </FormControl.Label>

              <div
                className={clsx(
                  'flex cursor-pointer p-4 rounded-md border',
                  !deleteOtherPosts && 'border-line',
                  deleteOtherPosts && 'border-focused bg-surface-pressed',
                )}
                onClick={() => setDeleteOtherPosts(v => !v)}
              >
                <div className="flex items-center">
                  <Checkbox checked={deleteOtherPosts} />
                </div>
                <div className="flex-grow flex flex-col">
                  <FormControl.Label className="cursor-pointer">
                    <T
                      defaultMessage="Delete all other posts"
                      id="PostRejectedModal.DeleteAllOtherPosts"
                    />
                  </FormControl.Label>
                </div>
              </div>
              <Alert
                className="mt-3 -mb-3 py-2"
                status="error"
                icon={<SvgIcon name="status-warning" />}
              >
                <T
                  defaultMessage="WARNING: Delete action is not reversible."
                  id="Moderation.Member.DeleteAllPosts.Warning"
                />
              </Alert>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button
          variant="primary"
          onClick={rejectContent}
          loading={loading}
          disabled={loading}
        >
          <T id="Generics.Confirm" defaultMessage="Confirm" />
        </Button>
        <Button variant="secondaryNeutral" onClick={onClose} disabled={loading}>
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
